import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { PublishResult } from "@/components/fleet/InviteBlocks";
import { VoiceInput } from "@/components/fleet/VoiceInput";
import { StepRoute, StepWhen, StepPrice, StepProgress, emptyRide } from "@/components/fleet/RideSteps";
import { GuestContact, guestValid } from "@/components/fleet/GuestContact";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/lib/i18n";
import { estimateRoute } from "@/lib/route";
import { RouteInfo } from "@/components/fleet/RouteInfo";

const emptyForm = () => ({ ...emptyRide(), guest_name: "", guest_email: "", guest_phone: "" });
const GUEST_KEYS = ["guest_name", "guest_email", "guest_phone"];

const STEPS = ["Tratta", "Data e Ora", "Prezzo"];

const canAdvance = (step, f, guest) =>
  (step === 0 && f.origin.trim().length >= 2 && f.destination.trim().length >= 2) ||
  (step === 1 && !!f.pickup_at) ||
  (step === 2 && Number(f.fare) > 0 && (!guest || guestValid(f)));

const PendingConfirm = ({ email, onDone }) => (
  <div data-testid="guest-pending-confirmation" className="flex flex-col gap-4">
    <p className="text-white">Questa email ha già un profilo FLEETFLOW. Ti abbiamo inviato a <b>{email}</b> un link: aprilo per confermare e pubblicare subito la corsa.</p>
    <button type="button" data-testid="guest-pending-done" onClick={onDone} className="ff-btn-primary h-14 uppercase">OK</button>
  </div>
);

export const ExpressRideModal = ({ open, onClose, onCreated }) => {
  const { user, setUser } = useAuth();
  const guest = !user;
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [pending, setPending] = useState(false);
  const [routing, setRouting] = useState(false);

  const routeKey = `${form.origin}|${form.destination}`;
  const routedKey = useRef("");
  useEffect(() => {
    if (step < 1 || routedKey.current === routeKey || form.origin.trim().length < 2 || form.destination.trim().length < 2) {
      return undefined;
    }
    let alive = true;
    setRouting(true);
    estimateRoute(form.origin, form.destination)
      .then((r) => { if (alive) { routedKey.current = routeKey; setForm((f) => ({ ...f, distance_km: r?.distance_km, duration_min: r?.duration_min })); } })
      .catch(() => {})
      .finally(() => alive && setRouting(false));
    return () => { alive = false; setRouting(false); };
  }, [step >= 1, routeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const handleClose = () => {
    setForm(emptyForm());
    setStep(0);
    setCreated(null);
    setPending(false);
    onClose();
  };

  const publish = async () => {
    setSaving(true);
    try {
      const { boost, ...rest } = form;
      const payload = { ...rest, fare: Number(form.fare) };
      let data;
      if (guest) {
        const { data: res } = await api.post("/rides/guest", payload);
        if (res.status === "pending_confirmation") {
          setPending(true);
          return;
        }
        setUser(res.user);
        data = res.ride;
      } else {
        GUEST_KEYS.forEach((k) => delete payload[k]);
        data = (await api.post("/rides", payload)).data;
      }
      onCreated();
      if (boost) {
        const { data: co } = await api.post(`/rides/${data.id}/boost-checkout`, { origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      }
      setCreated(data);
      toast.success(data.invite_enabled ? t("Corsa privata creata") : t("Corsa pubblicata sulla Live Board"));
    } catch (err) {
      toast.error(apiError(err, t("Pubblicazione non riuscita")));
    } finally {
      setSaving(false);
    }
  };

  const next = (e) => {
    e.preventDefault();
    if (!canAdvance(step, form, guest)) return;
    step < 2 ? setStep(step + 1) : publish();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent data-testid="express-ride-modal" className="ff-glass-panel sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl p-5 sm:p-7">
        <DialogHeader className="text-left">
          <DialogTitle className="ff-display text-2xl font-extrabold">{created ? t("Corsa pubblicata") : t("Carica Corsa")}</DialogTitle>
          <DialogDescription className="text-[#94A3B8] text-sm">{created ? t("Passala subito a un collega.") : `${t("Passo")} ${step + 1} ${t("di")} 3 • ${t(STEPS[step])}`}</DialogDescription>
        </DialogHeader>
        {pending ? (
          <PendingConfirm email={form.guest_email} onDone={handleClose} />
        ) : created ? (
          <PublishResult ride={created} onDone={handleClose} />
        ) : (
          <form onSubmit={next} className="flex flex-col gap-5" data-testid="express-ride-form">
            {!guest && <VoiceInput onParsed={(f) => { setForm((cur) => ({ ...cur, ...f })); setStep(f.fare ? 2 : f.pickup_at ? 1 : 0); }} />}
            <StepProgress step={step} labels={STEPS.map(t)} />
            {step === 0 && <StepRoute form={form} set={set} />}
            {step === 1 && <StepWhen form={form} set={set} />}
            {step === 2 && <RouteInfo km={form.distance_km} min={form.duration_min} loading={routing} testid="ride-route-estimate" />}
            {step === 2 && <StepPrice form={form} set={set} />}
            {step === 2 && guest && <GuestContact form={form} set={set} />}
            <div className="flex gap-2">
              {step > 0 && (
                <button type="button" data-testid="ride-step-back-button" onClick={() => setStep(step - 1)} className="ff-btn-secondary h-16 w-16 shrink-0" aria-label="Indietro"><ArrowLeft size={22} /></button>
              )}
              <button type="submit" disabled={saving || !canAdvance(step, form, guest)} data-testid={step < 2 ? "ride-step-next-button" : "submit-express-ride-button"} className="ff-btn-primary h-16 flex-1 uppercase text-base">
                {step < 2 ? t("Avanti") : saving ? t("Pubblicazione…") : form.boost ? t("Pubblica e paga € 2,99") : form.invite_enabled ? t("Pubblica e genera link") : t("Pubblica corsa")}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
